import { useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { useIntl } from 'react-intl';

import { getOrganizationDisplayName } from '@/common/organizations-flyout/src/utils';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftOrganizationSummary } from '@/state/actions';

type UseOrganizationBreadcrumbParams = {
	domainId: string;
	organizationId: string;
};

export const useOrganizationBreadcrumb = ({
	domainId,
	organizationId,
}: UseOrganizationBreadcrumbParams) => {
	const { formatMessage } = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const organizationDetail = useFastshiftSelector((state) => state.organizationDetail);
	const hasMatchingOrganizationDetail =
		organizationDetail.domainId === domainId && organizationDetail.orgId === organizationId;
	const organizationSummary = hasMatchingOrganizationDetail
		? organizationDetail.summary.data
		: null;
	const isLoading =
		!hasMatchingOrganizationDetail || (!organizationSummary && !organizationDetail.summary.error);

	useEffect(() => {
		if (
			!hasMatchingOrganizationDetail ||
			(!organizationDetail.summary.pending &&
				!organizationDetail.summary.error &&
				!organizationSummary)
		) {
			dispatch(
				getFastshiftOrganizationSummary({
					stargateClient,
					domainId,
					orgId: organizationId,
				}),
			);
		}
	}, [
		dispatch,
		domainId,
		hasMatchingOrganizationDetail,
		organizationDetail.summary.error,
		organizationDetail.summary.pending,
		organizationId,
		organizationSummary,
		stargateClient,
	]);

	return {
		isLoading,
		organizationName: getOrganizationDisplayName(
			{
				orgId: organizationId,
				orgName: organizationSummary?.orgName ?? null,
			},
			{ formatMessage },
		),
	};
};
